import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import AdvanceTableSearchBox from '../../../../components/common/advance-table/AdvanceTableSearchBox';
import { useAdvanceTableContext } from '../../../../providers/AdvanceTableProvider';

const FOCUS_AFTER_EXPAND_MS = 260;

const readGlobalFilter = (table) => {
	if (!table) return '';
	if (typeof table.getState === 'function') {
		return table.getState()?.globalFilter ?? '';
	}
	return table.globalFilter ?? '';
};

/** 待办搜索：默认图标，展开后输入；有关键词时可收起保留筛选 */
const PendingListToolbarSearchToggle = ({ placeholder = '搜索', variant = 'toolbar' }) => {
	const table = useAdvanceTableContext();
	const globalFilter = readGlobalFilter(table);
	const hasFilter = Boolean(String(globalFilter).trim());
	const [expanded, setExpanded] = useState(false);
	const rootRef = useRef(null);
	const focusTimerRef = useRef(null);
	const hadFilterRef = useRef(hasFilter);
	const isOverlay = variant === 'overlay';

	const focusSearchInput = useCallback(() => {
		const input = rootRef.current?.querySelector('input[type="search"], input[type="text"], input#search');
		input?.focus();
		input?.select?.();
	}, []);

	const blurSearchInput = useCallback(() => {
		const input = rootRef.current?.querySelector('input[type="search"], input[type="text"], input#search');
		input?.blur();
	}, []);

	const collapseSearch = useCallback(() => {
		if (focusTimerRef.current) {
			clearTimeout(focusTimerRef.current);
			focusTimerRef.current = null;
		}
		blurSearchInput();
		setExpanded(false);
	}, [blurSearchInput]);

	useEffect(() => {
		return () => {
			if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
		};
	}, []);

	useEffect(() => {
		if (hasFilter && !hadFilterRef.current) {
			setExpanded(true);
		}
		hadFilterRef.current = hasFilter;
	}, [hasFilter]);

	// 点外部收起（保留关键词）；有筛选时也可收起，避免挡住表头
	useEffect(() => {
		if (!expanded) return undefined;
		const handlePointerDown = (event) => {
			if (rootRef.current?.contains(event.target)) return;
			if (event.target.closest('.top-courses-tab-scroll, .processing-order-card .nav-tabs')) return;
			collapseSearch();
		};
		document.addEventListener('pointerdown', handlePointerDown);
		return () => document.removeEventListener('pointerdown', handlePointerDown);
	}, [expanded, collapseSearch]);

	useEffect(() => {
		if (!expanded) return undefined;
		const handleKeyDown = (event) => {
			if (event.key !== 'Escape') return;
			event.preventDefault();
			collapseSearch();
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [expanded, collapseSearch]);

	const handleToggle = () => {
		if (isOverlay && expanded) return;
		setExpanded((open) => {
			const next = !open;
			if (focusTimerRef.current) {
				clearTimeout(focusTimerRef.current);
				focusTimerRef.current = null;
			}
			if (next) {
				focusTimerRef.current = setTimeout(() => {
					focusSearchInput();
					focusTimerRef.current = null;
				}, FOCUS_AFTER_EXPAND_MS);
			} else {
				blurSearchInput();
			}
			return next;
		});
	};

	return (
		<div
			ref={rootRef}
			className={classNames('filter-toolbar-search-toggle pending-list-toolbar-search-toggle', {
				'is-expanded': expanded,
				'pending-list-toolbar-search-toggle--overlay': isOverlay,
				'has-filter': hasFilter,
				'btn-reveal-trigger': isOverlay && !expanded,
			})}
		>
			{!(isOverlay && expanded) ? (
				<Button
					type='button'
					variant={isOverlay ? 'reveal' : 'outline-secondary'}
					size='sm'
					className={classNames('filter-toolbar-search-toggle__btn pending-list-toolbar-search-toggle__btn', {
						'border-300': !isOverlay,
						'btn-reveal text-600': isOverlay,
						'is-active': hasFilter,
					})}
					onClick={handleToggle}
					onMouseLeave={(event) => {
						if (event.currentTarget === document.activeElement) {
							event.currentTarget.blur();
						}
					}}
					aria-expanded={expanded}
					aria-label={hasFilter ? '展开搜索（已有筛选）' : '展开搜索'}
					title={hasFilter ? '展开搜索（已有筛选）' : '搜索'}
				>
					<FontAwesomeIcon icon='search' className='filter-toolbar-search-toggle__icon' />
					{hasFilter && !expanded ? <span className='filter-toolbar-search-toggle__dot pending-list-toolbar-search-toggle__dot' aria-hidden /> : null}
				</Button>
			) : null}
			<div className='filter-toolbar-search-toggle__field pending-list-toolbar-search-toggle__field'>
				<AdvanceTableSearchBox
					placeholder={isOverlay ? '搜索订单、客户…' : placeholder}
					trailing='clear-only'
					type={isOverlay ? 'text' : 'search'}
				/>
			</div>
		</div>
	);
};

PendingListToolbarSearchToggle.propTypes = {
	placeholder: PropTypes.string,
	variant: PropTypes.oneOf(['toolbar', 'overlay']),
};

export default PendingListToolbarSearchToggle;
